import React, { useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { prefetchSupervisorList, cancelTicketPrefetch } from "../apis/ticketPrefetch";
import { getQueueFilterSessionStorageKey, readQueueFilterSessionState, parseQueueUrlState } from "../utils/queueUrlState";
import { isAutotask, isNinjaOne } from "../utils/mspUtils";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import SupervisorApi from "../apis/supervisorApi";
import { useAppSelector } from "../hooks";
import { useUserMsp } from "../hooks/useUserMsp";
import { TechnicianGroup } from "../types";
import { NEUTRAL, SELECTED_EDGE, VIOLET } from "../styles/brandTokens";
import { ticketLookupText } from "../utils/ticketLookupQuery";
import {
  getCategoryScopeLabelPlural,
  getTicketScopeLabelPlural,
} from "../utils/mspUtils";
import {
  OPEN_TICKETS_LABEL,
  OPEN_TICKETS_PATH,
  WorkScopeItem,
  buildCategoryScopeItems,
  buildQueueScopeItems,
} from "./workScopes";

interface WorkScopeNavProps {
  categories?: Array<{ groupId: string; name: string }>;
}

type TicketLookupState = { status: "loading" } | { status: "error"; message: string } | null;

function ticketLookupErrorMessage(error: unknown, text: string): string {
  const err = error as { response?: { status?: number }; message?: string };
  if (err?.response?.status === 404) return `No ticket matches ${text}`;
  return err?.message || "Could not look up this ticket";
}

const WorkScopeNav: React.FC<WorkScopeNavProps> = ({ categories = [] }) => {
  const location = useLocation();
  const { userMspInfo } = useUserMsp();
  const { user } = useAuth0();
  const { technicianGroupList, ninjaoneBoardList } = useAppSelector((state) => state.supervisor);
  const navigate = useNavigate();
  const supervisorApi = useMemo(() => new SupervisorApi(), []);
  const [query, setQuery] = useState("");
  const [ticketLookup, setTicketLookup] = useState<TicketLookupState>(null);
  const lookupSeq = useRef(0);

  const queues = useMemo(
    () =>
      buildQueueScopeItems(
        userMspInfo?.msp_type,
        Array.isArray(technicianGroupList) ? (technicianGroupList as TechnicianGroup[]) : [],
        Array.isArray(ninjaoneBoardList) ? ninjaoneBoardList : []
      ),
    [ninjaoneBoardList, technicianGroupList, userMspInfo?.msp_type]
  );

  const categoryScopes = useMemo(() => buildCategoryScopeItems(categories), [categories]);

  const normalizedQuery = query.trim().toLowerCase();
  const matches = (item: WorkScopeItem) => !normalizedQuery || item.text.toLowerCase().includes(normalizedQuery);
  const visibleQueues = queues.filter(matches);
  const visibleCategories = categoryScopes.filter(matches);
  const showOpenTickets = !normalizedQuery || OPEN_TICKETS_LABEL.toLowerCase().includes(normalizedQuery);
  const ticketText = ticketLookupText(query);

  const queueLabel = getTicketScopeLabelPlural(userMspInfo?.msp_type);
  const categoryLabel = getCategoryScopeLabelPlural(userMspInfo?.msp_type);
  const openTicketsSelected = location.pathname === OPEN_TICKETS_PATH;

  const changeQuery = (next: string) => {
    lookupSeq.current += 1;
    setQuery(next);
    setTicketLookup(null);
  };

  // Resolve the typed ID with the PSA, then open the ticket view (AI chat + ticket pane).
  const openTicket = async () => {
    if (!ticketText || ticketLookup?.status === "loading") return;
    const seq = ++lookupSeq.current;
    setTicketLookup({ status: "loading" });
    try {
      const match = await supervisorApi.resolveTicket(ticketText);
      if (seq !== lookupSeq.current) return;
      changeQuery("");
      const returnTo = `${location.pathname}${location.search}`;
      const search = returnTo.startsWith("/supervisor") ? `?${new URLSearchParams({ returnTo })}` : "";
      navigate(`/supervisor/tickets/${encodeURIComponent(match.ticketId)}${search}`);
    } catch (error) {
      if (seq !== lookupSeq.current) return;
      setTicketLookup({ status: "error", message: ticketLookupErrorMessage(error, ticketText) });
    }
  };

  const prefetchScope = (item: WorkScopeItem) => {
    const open = item.path === OPEN_TICKETS_PATH;
    const mspType = userMspInfo?.msp_type;
    const session = readQueueFilterSessionState(getQueueFilterSessionStorageKey(user?.sub || user?.email, userMspInfo?.msp_id));
    const state = parseQueueUrlState("", undefined, session);
    const board = isNinjaOne(mspType || "") && Array.isArray(ninjaoneBoardList)
      ? ninjaoneBoardList.find((b: any) => open ? b.name?.toLowerCase() === "all tickets" : b.name === item.text) : null;
    prefetchSupervisorList(mspType, {
      groupName: open ? board?.name : (item.path.includes("/categories/") ? undefined : item.text),
      categoryName: item.path.includes("/categories/") ? [item.text] : undefined,
      boardId: board?.id,
      ...(open ? {} : { status: state.status, customer: state.customer, includeAllStatuses: isAutotask(mspType || "") }),
    });
  };

  return (
    <Box
      component="nav"
      aria-label={`Tickets by ${queueLabel}`}
      sx={{
        width: 248,
        flex: "0 0 248px",
        alignSelf: "stretch",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        borderRight: `1px solid ${NEUTRAL.border}`,
        // The plum tint is the page canvas only. This is a panel sitting on it,
        // so it takes the surface colour like every other panel.
        bgcolor: NEUTRAL.surface,
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.75, pb: 1 }}>
        <Typography
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: 13,
            fontWeight: 700,
            color: NEUTRAL.text,
            mb: 1,
          }}
        >
          Tickets by {queueLabel}
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(event) => changeQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && ticketText) {
              event.preventDefault();
              void openTicket();
            }
          }}
          placeholder={`Search ${queueLabel.toLowerCase()} or ticket ID`}
          inputProps={{ "aria-label": `Search ${queueLabel.toLowerCase()} or ticket ID` }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: NEUTRAL.textMuted }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: "#fff",
              borderRadius: "6px",
              fontFamily: "var(--theo-font-sans)",
              fontSize: 13,
            },
          }}
        />
      </Box>
      <Box sx={{ overflowY: "auto", flex: 1, px: 0.75, pb: 1.5 }}>
        {ticketText ? (
          <TicketLookupRow text={ticketText} state={ticketLookup} onOpen={() => void openTicket()} />
        ) : null}
        {showOpenTickets ? (
          <ScopeLink item={{ text: OPEN_TICKETS_LABEL, path: OPEN_TICKETS_PATH }} selected={openTicketsSelected} onIntent={prefetchScope} />
        ) : null}
        {visibleQueues.map((item) => (
          <ScopeLink key={item.path} item={item} selected={location.pathname === item.path} onIntent={prefetchScope} />
        ))}
        {categoryScopes.length > 0 && (visibleCategories.length > 0 || !normalizedQuery) ? (
          <Typography
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: NEUTRAL.textMuted,
              px: 1,
              pt: 1.5,
              pb: 0.5,
            }}
          >
            {categoryLabel}
          </Typography>
        ) : null}
        {visibleCategories.map((item) => (
          <ScopeLink key={item.path} item={item} selected={location.pathname === item.path} onIntent={prefetchScope} />
        ))}
        {normalizedQuery && !ticketText && !showOpenTickets && visibleQueues.length === 0 && visibleCategories.length === 0 ? (
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: NEUTRAL.textSecondary, px: 1, py: 0.5 }}>
            No matches.
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

const TicketLookupRow: React.FC<{ text: string; state: TicketLookupState; onOpen: () => void }> = ({
  text,
  state,
  onOpen,
}) => {
  const loading = state?.status === "loading";
  return (
    <Box sx={{ mb: 0.75 }}>
      <ButtonBase
        onClick={onOpen}
        disabled={loading}
        aria-label={`Open ticket ${text}`}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          gap: 1,
          px: 1,
          py: 0.7,
          borderRadius: "6px",
          color: NEUTRAL.text,
          bgcolor: VIOLET.tint,
          fontFamily: "var(--theo-font-sans)",
          fontSize: 13.5,
          fontWeight: 600,
          textAlign: "left",
          transition: "background-color 150ms ease",
          "&:hover": { bgcolor: "#EEE8F5" },
          "&:focus-visible": { outline: `2px solid ${VIOLET.main}`, outlineOffset: -2 },
        }}
      >
        {loading ? (
          <CircularProgress size={14} thickness={5} sx={{ color: VIOLET.main, flexShrink: 0 }} />
        ) : (
          <ConfirmationNumberOutlinedIcon sx={{ fontSize: 16, color: VIOLET.main, flexShrink: 0 }} />
        )}
        <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          Open ticket {text}
        </Box>
        <Box component="span" sx={{ ml: "auto", fontSize: 11, fontWeight: 500, color: NEUTRAL.textMuted, flexShrink: 0 }}>
          Enter
        </Box>
      </ButtonBase>
      {state?.status === "error" ? (
        <Typography
          role="alert"
          sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12.5, color: NEUTRAL.textSecondary, px: 1, pt: 0.5 }}
        >
          {state.message}
        </Typography>
      ) : null}
    </Box>
  );
};

const ScopeLink: React.FC<{ item: WorkScopeItem; selected: boolean; onIntent: (item: WorkScopeItem) => void }> = ({ item, selected, onIntent }) => (
  <Box
    component={RouterLink}
    to={item.path}
    onMouseEnter={() => { if (!selected) onIntent(item); }}
    onMouseLeave={cancelTicketPrefetch}
    onFocus={() => { if (!selected) onIntent(item); }}
    onBlur={cancelTicketPrefetch}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1,
      py: 0.7,
      mb: 0.25,
      borderRadius: "6px",
      textDecoration: "none",
      color: selected ? NEUTRAL.text : NEUTRAL.textSecondary,
      bgcolor: selected ? VIOLET.tint : "transparent",
      boxShadow: selected ? SELECTED_EDGE : "none",
      fontFamily: "var(--theo-font-sans)",
      fontSize: 13.5,
      fontWeight: selected ? 700 : 500,
      transition: "background-color 150ms ease, color 150ms ease",
      "&:hover": { bgcolor: selected ? VIOLET.tint : "#EEE8F5", color: NEUTRAL.text },
      "&:focus-visible": { outline: `2px solid ${VIOLET.main}`, outlineOffset: -2 },
    }}
    aria-current={selected ? "page" : undefined}
  >
    <Box
      component="span"
      sx={{
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {item.text}
    </Box>
  </Box>
);

export default WorkScopeNav;
